import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ban,
  Eye,
  FileText,
  Loader2,
  Lock,
  MessageCircle,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Truck,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  fetchContainerLines,
  createContainerLine,
  deleteContainerLine,
  cancelContainerLine,
  restoreContainerLine,
} from "../../store/slices/containerOrdersSlice";
import { getCountries } from "../../store/slices/companySlice";
import { LineProgressBar } from "./LineProgressBar";
import ActionMenu from "../UI/ActionMenu";
import { useConfirm } from "../../hooks/useConfirm";
import { UNSTARTED_LINE_STATUSES } from "../../types/containerOrders";
import {
  activeStopsOf,
  completedStepCount,
  lineSteps,
} from "../../utils/containerLineSteps";
import { blockingRequirements } from "../../utils/containerLineStart";
import { downloadContainerCmrPdf } from "../../utils/containerCmr";
import type { CmrOrderContext } from "../../utils/containerCmr";
import type { ContainerOrderLine } from "../../types/containerOrders";
import { lineRouteStops } from "../../utils/containerOrderRoute";
import RouteChain from "../containerOrders/RouteChain";
import { vehicleLabel } from "../../utils/vehicleLabel";
import HandlerAssign from "../handler/HandlerAssign";
import { containerOrderAPI } from "../../services/api";

interface OrderLinesPanelProps {
  orderId: string;
  /**
   * The order these containers belong to, when the caller already holds it.
   *
   * Only the CMR actions need it, and only for the order's direction — which
   * decides which leg of a container's three-stop run the consignment note
   * describes. Optional because the note prints without it, reading the run
   * end to end; both call sites do have the order, so in practice it is
   * always passed.
   */
  order?: CmrOrderContext;
  /** Bumped by the parent after a bulk generate, to force a refetch. */
  refreshToken?: number;
  /**
   * The list's search box, when this order was found through one of its
   * containers. The panel then shows only the containers that matched — the
   * point of opening the row was to see which one, and the rest of the batch
   * buries it. Absent everywhere else, which is the whole batch as before.
   */
  search?: string;
  /**
   * The list's handler scope, when the row was drawn under one.
   *
   * Narrows these rows the same way `search` does — see `fetchContainerLines`.
   * Absent on the order's own page, which is never scoped: somebody who has
   * opened a booking is looking at that booking, not at a slice of it.
   */
  handlerScope?: string;
  onGenerate: () => void;
  canGenerate: boolean;
  /** Called after the panel changes the batch, so the parent can re-read. */
  onBatchChanged?: () => void;
  note?: string;
}

const PAGE_SIZE = 25;

/**
 * Whether a container can still be changed.
 *
 * The single source of truth for the row's icon *and* the page's mode, so the
 * two can never disagree: a completed container is a record of the run, a
 * cancelled one was called off, and a locked order is not editable at all. The
 * server refuses the write in each case regardless.
 */
function isViewOnly(line: ContainerOrderLine, canEdit: boolean): boolean {
  return !canEdit || line.status === "cancelled" || line.status === "completed";
}

const named = (
  ref: { firstName?: string; lastName?: string } | string | null | undefined,
) =>
  ref && typeof ref === "object"
    ? `${ref.firstName ?? ""} ${ref.lastName ?? ""}`.trim()
    : "";

/** What the table shows where a container has nothing to show. */
const EMPTY = "—";

/** The container's own cut-off, with its time when one was given. */
const cutOffOf = (line: ContainerOrderLine): string | null => {
  if (!line.cutOffDate) return null;
  const date = new Date(line.cutOffDate).toLocaleDateString();
  return line.cutOffTime ? `${date} ${line.cutOffTime}` : date;
};

/** A stable empty list, so an order with no bucket yet does not re-render. */
const NO_LINES: ContainerOrderLine[] = [];

/**
 * The containers on one order.
 *
 * Rendered both under the order detail page and inside an expanded row of the
 * orders list, so it owns its own fetching rather than taking rows as a prop —
 * and reads them out of a bucket keyed by that order, because the list can have
 * several of these on screen at once.
 */
export function OrderLinesPanel({
  orderId,
  order,
  refreshToken = 0,
  search,
  handlerScope,
  onGenerate,
  canGenerate,
  onBatchChanged,
  note,
}: OrderLinesPanelProps) {
  const { t } = useTranslation("containerOrders");
  const { confirm, confirmDialog } = useConfirm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  /**
   * The country list, for the CMR's address boxes.
   *
   * A stop stores its country as an id, and the note prints the name — so
   * without this the three country lines come out blank on an otherwise
   * complete document. Fetched here rather than assumed present: this panel is
   * rendered inside the orders list, which has no other reason to have loaded
   * them, and the thunk is a no-op once they are in the store.
   */
  const countries = useAppSelector((state) => state.company.countries);
  const [downloadingCmr, setDownloadingCmr] = useState<string | null>(null);

  useEffect(() => {
    if (!countries?.length) dispatch(getCountries());
  }, [dispatch, countries?.length]);

  /**
   * One container's consignment note, as a file.
   *
   * Every field on it is optional — a container is filled in across three
   * stops, so the note is routinely printed off a part-written record and a
   * missing value prints as the blank box it is on the paper form. That is why
   * this is offered on every row rather than gated the way the shipment
   * table's CMR is: there is no state of a container in which the note cannot
   * be drawn.
   */
  const handleDownloadCmr = async (line: ContainerOrderLine) => {
    if (downloadingCmr) return;
    try {
      setDownloadingCmr(line._id);
      await downloadContainerCmrPdf(line, order, countries ?? []);
    } catch {
      toast.error(t("lineCmr.downloadFailed"));
    } finally {
      setDownloadingCmr(null);
    }
  };

  // This order's containers, and only this order's. The orders list expands any
  // number of rows at once and every one of them renders this component; while
  // the store held a single `lines` array, two open panels raced and the later
  // response painted its containers into the earlier panel's table — under that
  // order's progress bar, with its edit and delete buttons pointed at them.
  const entry = useAppSelector(
    (state) => state.containerOrders.linesByOrder[orderId],
  );

  const lines = entry?.lines ?? NO_LINES;
  const lineProgress = entry?.progress ?? null;
  const matchedTotal = entry?.matchedTotal ?? null;
  const query = search?.trim() ?? "";
  const scope = handlerScope ?? "";
  // Either narrowing makes these rows a result rather than the whole batch, so
  // the panel's "showing N of M" banner and its hidden Add button key on both.
  const filtering = !!query || !!scope;
  const linesLoading = entry?.loading ?? false;
  const loadFailed = !!entry?.error;
  const loadedPages = entry?.loadedPages ?? 0;
  const staleTick = entry?.staleTick ?? 0;

  /**
   * A container opens on its own page, not in a modal over this one.
   *
   * It outgrew the modal — a container now carries its whole three-stop route as
   * well as the box, its packing list and its assignment — and a page gives it
   * the thing a modal never had: a URL. This panel is rendered under the order
   * *and* inside an expanded row of the orders list, and both get the same
   * navigation for free.
   */
  const openLine = (line: ContainerOrderLine) =>
    navigate(`/container-orders/${orderId}/container/${line._id}`);

  const load = useCallback(
    (page: number) =>
      dispatch(
        fetchContainerLines({
          orderId,
          page,
          limit: PAGE_SIZE,
          search: query,
          handler: scope,
        }),
      ),
    // `query` included so that editing the search box re-reads the batch
    // through the new one, rather than leaving the previous result on screen
    // under a search that no longer produced it. `scope` for the same reason:
    // switching the toolbar filter has to re-read an already-open panel.
    [dispatch, orderId, query, scope],
  );

  // Mount, an explicit refresh from the parent, or an invalidation from
  // elsewhere in the store — cancelling the order cascades to its containers,
  // so the rows held here stop being true without this panel doing anything.
  useEffect(() => {
    load(1);
  }, [load, refreshToken, staleTick]);

  const handleAddOne = async () => {
    try {
      // The store appends the new container and adjusts the tallies from the
      // response, so nothing here has to re-read the batch. Only the parent
      // order is told: its own count and derived status did change.
      await dispatch(createContainerLine({ orderId, data: {} })).unwrap();
      toast.success(t("toasts.lineAdded"));
      onBatchChanged?.();
    } catch (error: any) {
      toast.error(typeof error === "string" ? error : t("toasts.lineFailed"));
    }
  };

  /**
   * What cancelling or removing this container would cost, in money.
   *
   * Cancelling means not chargeable — the rule is the client's and it is
   * absolute: a cancelled container never reaches the invoicing picker, and a
   * draft naming one cannot be locked. The consequence the rule does not state
   * is that every charge standing on the box when it is cancelled stops being
   * billable, permanently, at that instant.
   *
   * On a container called off before it moved that is nothing — there is
   * nothing on it. On one cancelled at the loading site it can be a lift, two
   * waiting hours and a customs call that were genuinely earned. Those are the
   * cancellations this figure exists for, and it is shown *before* the decision
   * rather than discovered afterwards by an invoice that came up short.
   *
   * Computed here rather than fetched: the panel already holds every container
   * with its charge rows, and a round trip for arithmetic over data on screen
   * would only add a way for the two to disagree. Invoiced rows are skipped —
   * they are not a loss, they are already on an invoice, and a container
   * carrying one cannot be cancelled at all.
   */
  const unbilledCharges = (line: ContainerOrderLine) => {
    const rows = (line.services ?? []).filter((svc) => !svc.invoiceId);
    // Quantity × price, the amount an invoice would bill — not the stored
    // `total`, which can be stale (the server reads it the same way).
    return {
      count: rows.length,
      total:
        Math.round(
          rows.reduce(
            (sum, svc) =>
              sum + (Number(svc.qty) || 1) * (Number(svc.price) || 0),
            0,
          ) * 100,
        ) / 100,
    };
  };

  /**
   * Whether this container is holding a place in some truck's week.
   *
   * A boolean rather than a count: the client's view of `planning` carries only
   * the first cell, while a container may hold several. The server knows the
   * real number and says it in the toast afterwards; promising a figure here
   * that the next sentence contradicts would be worse than not promising one.
   */
  const holdsSlot = (line: ContainerOrderLine) => !!line.planning?.date;

  /**
   * The body of a cancel or remove question: what happens, itemised.
   *
   * A list rather than a paragraph, because these are separate consequences
   * landing on separate people — the driver loses a job, the board gains a slot,
   * the invoice loses a line — and a dispatcher scanning a dialog at speed reads
   * a list and skims a paragraph.
   *
   * Only the consequences that actually apply are listed. A container nobody
   * planned has no slot to release and no driver to stop, and padding the dialog
   * with "0 slots released" trains people to stop reading it.
   */
  const consequences = (items: Array<string | null>) => (
    <div className="space-y-2">
      <ul className="list-disc space-y-1 pl-4">
        {items.filter(Boolean).map((item) => (
          <li key={item as string}>{item}</li>
        ))}
      </ul>
    </div>
  );

  const handleCancel = async (line: ContainerOrderLine) => {
    const charges = unbilledCharges(line);
    const onBoard = holdsSlot(line);
    const started = !UNSTARTED_LINE_STATUSES.includes(line.status);

    const agreed = await confirm({
      title: t("confirm.titles.cancelLine", { lineNumber: line.lineNumber }),
      description: consequences([
        started ? t("linesBlock.cancelBody.started") : null,
        line.vehicleId ? t("linesBlock.cancelBody.driver") : null,
        onBoard ? t("linesBlock.cancelBody.slots") : null,
        charges.count
          ? t("linesBlock.cancelBody.charges", {
              count: charges.count,
              amount: charges.total.toFixed(2),
            })
          : t("linesBlock.cancelBody.noCharges"),
        t("linesBlock.cancelBody.restore"),
      ]),
      confirmLabel: t("confirm.actions.cancelLine"),
      cancelLabel: t("confirm.actions.keep"),
      // Warning rather than danger: this is reversible, and reserving the red
      // treatment for what is not keeps it meaning something.
      tone: "warning",
    });
    if (!agreed) return;

    try {
      const { message } = await dispatch(
        cancelContainerLine({
          orderId,
          lineId: line._id,
          updatedAt: line.updatedAt,
        }),
      ).unwrap();
      // The server's own sentence: it is the only one that knows how many board
      // slots actually came back.
      toast.success(message);
      onBatchChanged?.();
    } catch (error: any) {
      toast.error(typeof error === "string" ? error : t("toasts.lineFailed"));
    }
  };

  const handleRestore = async (line: ContainerOrderLine) => {
    const agreed = await confirm({
      title: t("confirm.titles.restoreLine", { lineNumber: line.lineNumber }),
      description: consequences([
        t("linesBlock.restoreBody.status"),
        // The one thing people get wrong about restoring, said plainly. The
        // slot was released when the container was cancelled and the week has
        // very likely been planned around the gap since.
        t("linesBlock.restoreBody.pool"),
      ]),
      confirmLabel: t("confirm.actions.restoreLine"),
      cancelLabel: t("confirm.actions.keep"),
      tone: "neutral",
    });
    if (!agreed) return;

    try {
      const { message } = await dispatch(
        restoreContainerLine({
          orderId,
          lineId: line._id,
          updatedAt: line.updatedAt,
        }),
      ).unwrap();
      toast.success(message);
      onBatchChanged?.();
    } catch (error: any) {
      toast.error(typeof error === "string" ? error : t("toasts.lineFailed"));
    }
  };

  /**
   * Removing is the irreversible one, and the dialog has to say which it is.
   *
   * Cancel and Remove sit next to each other in this menu and read as near
   * synonyms; the difference is everything. Cancelling closes a container and
   * keeps it — the timeline, the documents, the charges, the fact that it
   * existed — and can be undone. Removing takes the record away, and the only
   * reason it is allowed at all is that the container has never been driven and
   * has never been billed, so there is no record to lose.
   *
   * Both of those are stated, because "this cannot be undone" on its own is a
   * sentence people have learned to click through.
   */
  const handleRemove = async (line: ContainerOrderLine) => {
    const charges = unbilledCharges(line);

    const agreed = await confirm({
      title: t("confirm.titles.removeLine", { lineNumber: line.lineNumber }),
      description: consequences([
        t("linesBlock.removeBody.permanent"),
        holdsSlot(line) ? t("linesBlock.removeBody.slots") : null,
        charges.count
          ? t("linesBlock.removeBody.charges", {
              count: charges.count,
              amount: charges.total.toFixed(2),
            })
          : null,
        t("linesBlock.removeBody.alternative"),
      ]),
      confirmLabel: t("confirm.actions.removeLine"),
      cancelLabel: t("confirm.actions.keep"),
      tone: "danger",
    });
    if (!agreed) return;

    try {
      await dispatch(
        deleteContainerLine({ orderId, lineId: line._id }),
      ).unwrap();
      toast.success(t("toasts.lineRemoved"));
      onBatchChanged?.();
    } catch (error: any) {
      toast.error(typeof error === "string" ? error : t("toasts.lineFailed"));
    }
  };

  // Editing a container now happens on its own page, so there is no in-place
  // save to reconcile here: this panel unmounts on the way there and re-reads
  // the batch on the way back, which also picks up anything the edit moved —
  // the tallies, the parent order's derived status — without a special case.

  // Only the very first read shows a spinner instead of the table. Every later
  // one leaves the rows where they are: blanking them unmounted every expanded
  // panel on the page, and took whatever was open inside them with it.
  if (linesLoading && lines.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-[#8A9590]">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("linesBlock.loading")}
      </div>
    );
  }

  if (loadFailed && lines.length === 0) {
    return (
      <div className="space-y-2 py-6 text-center">
        <p className="text-sm text-[#E24B4A]">{t("linesBlock.loadFailed")}</p>
        <button
          type="button"
          onClick={() => load(1)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-[#F5F4F0]"
        >
          {t("linesBlock.retry")}
        </button>
      </div>
    );
  }

  // What "load more" pages against: the matched rows while a search is on, the
  // whole batch otherwise.
  const total = filtering
    ? (matchedTotal ?? lines.length)
    : (lineProgress?.total ?? lines.length);
  const th =
    "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[#8A9590]";
  // `align-top` because the route cell wraps: with the rest centred
  // against it, a tall row reads as if its values had come loose.
  const td = "px-3 py-2 align-top text-sm text-[#0B0F0E]";
  /**
   * Columns that must never be read as a guess.
   *
   * Which box, how far along, and what can be done to it are the reasons anyone
   * opens this table, and half a container number is worse than none — it looks
   * like a container number. So these are given a width that fits their longest
   * real value and the table scrolls instead, which it already did at 720px;
   * only the three free-text columns are expected to give way, and they say
   * what they clipped on hover.
   */
  const tdFixed = `${td} truncate`;
  /** Names, which are long, arbitrary, and safe to clip mid-word. */
  const tdClip = `${td} truncate`;
  // The two are the same declaration and differ only in the width their column
  // is given above — which is the whole mechanism. What keeps a container
  // number whole is its 118px, not a class; the ellipsis is a backstop so that
  // if a column is ever squeezed below its content it clips cleanly instead of
  // spilling the text across the cell beside it, which is how a fixed-layout
  // table fails when told never to wrap.

  return (
    <div className="space-y-3">
      {!!lineProgress && (
        <LineProgressBar
          byStatus={lineProgress.byStatus}
          total={lineProgress.total}
          cancelled={lineProgress.cancelled}
        />
      )}

      {note && <p className="text-[12px] text-[#8A9590]">{note}</p>}

      {/* A failed refresh over rows that are already on screen: said here,
          quietly, rather than by replacing the table with an error. What is
          shown is still the last good answer. */}
      {loadFailed && lines.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#FCE9E9] px-3 py-2 text-[12px] text-[#A63A39]">
          <span>{t("linesBlock.refreshFailed")}</span>
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded border border-[#E3B7B6] px-2 py-0.5 hover:bg-white"
          >
            {t("linesBlock.retry")}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[13px] text-[#8A9590]">
          {/* The batch's own count, and only when the panel is showing the
              batch. Under a search these rows are a result, not a tally, and
              the number of them is the number of rows on screen. */}
          {!filtering && t("linesBlock.countLabel", { count: total })}
          {/* A refresh over rows that are already there. The spinner sits beside
              the count instead of replacing the table — emptying it unmounted
              every expanded panel and everything open inside them. */}
          {linesLoading && (
            <Loader2
              className="h-3 w-3 animate-spin"
              aria-label={t("linesBlock.loading")}
            />
          )}
        </span>
        {/* Adding to the batch is hidden while the panel is showing a search
            result: a new container would appear among rows that matched
            something it does not, and the button is one scroll away on the
            order's own page. */}
        {canGenerate && !filtering && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleAddOne}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-[#F5F4F0]"
            >
              <Plus className="h-4 w-4" />
              {t("linesBlock.addOne")}
            </button>
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
            >
              {lines.length
                ? t("linesBlock.generateMore")
                : t("linesBlock.generate")}
            </button>
          </div>
        )}
      </div>

      {lines.length === 0 ? (
        <p className="py-6 text-center text-sm text-[#8A9590]">
          {filtering
            ? t("linesBlock.emptyForSearch", { query })
            : t("linesBlock.empty")}
        </p>
      ) : (
        /* `@container` is load-bearing, not decoration.
           
           The table below declares a min-width, and a min-width escapes: an
           ancestor that sizes to its content adopts it and hands it further up
           still. This panel renders inside a `<td colSpan={13}>` of the orders
           list, and a table cell is exactly such an ancestor — so the 1420px
           landed on the orders table, which grew to match and gave the whole
           page a horizontal scrollbar, stretching all thirteen order columns
           with it. `container-type: inline-size` makes this div's width a
           function of its parent rather than its contents, which drops its
           min-content contribution to zero and stops the width escaping. The
           table then scrolls inside it, which is what `overflow-x-auto` was
           always meant to do here. */
        <div className="@container overflow-x-auto">
          {/* The floor is set to fit, not merely to be wide.
           
              Ten columns need room, but demanding more than the page can
              give means every screen scrolls every time — so the floor is sized
              against the narrowest place this table is actually read: inside
              the detail page's card, which on a 1440px window leaves roughly
              1376px once the page's max width and the card's padding are taken
              off. 1208px fits there with room to spare, and still gives each
              column its natural width. Below that the wrapper scrolls, as it
              already did at 720px. */}
          <table className="w-full min-w-[1208px] table-fixed border-collapse">
            {/* The widths add up to the table's own minimum, so at that floor
                every column gets exactly what it asks for and nothing is
                squeezed into a guess; wider than the floor they scale up
                together. Fixed layout rather than automatic because content
                must not decide this — one container with a long terminal name
                would otherwise widen its column by starving the rest. */}
            <colgroup>
              <col className="w-[84px]" />
              {/* 118px less 24px of padding is ~94px of text — an eleven-
                  character container number at 14px, which is the longest one
                  there is. This column is why the floor exists. */}
              <col className="w-[118px]" />
              <col className="w-[64px]" />
              {/* Widened from 172px when the column stopped summarising the
                  route and started listing it: at the old width a three-stop
                  run wrapped to four lines. */}
              <col className="w-[240px]" />
              <col className="w-[134px]" />
              <col className="w-[106px]" />
              <col className="w-[156px]" />
              <col className="w-[76px]" />
              <col className="w-[150px]" />
              <col className="w-[80px]" />
            </colgroup>
            <thead className="border-b border-[rgba(11,15,14,0.08)]">
              <tr>
                <th className={th}>{t("linesBlock.columns.line")}</th>
                <th className={th}>{t("linesBlock.columns.number")}</th>
                <th className={th}>{t("linesBlock.columns.containerType")}</th>
                <th className={th}>{t("linesBlock.columns.route")}</th>
                <th className={th}>{t("linesBlock.columns.loadingSite")}</th>
                <th className={th}>{t("linesBlock.columns.cutOff")}</th>
                <th className={th}>{t("linesBlock.columns.truckDriver")}</th>
                <th className={th}>{t("linesBlock.columns.handler")}</th>
                <th className={th}>{t("linesBlock.columns.vgm")}</th>
                <th className={th}>{t("linesBlock.columns.progress")}</th>
                {/* Blank to the eye — a column of icons needs no heading over
                    it — but not to a screen reader, which otherwise announces
                    the row's last cell as belonging to nothing. */}
                <th className={th}>
                  <span className="sr-only">
                    {t("linesBlock.columns.actions")}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                // Both off the container's own route: two boxes on one order
                // can have different runs, so "3/6" from a constant would be a
                // wrong number as often as a right one.
                const steps = lineSteps(line);
                const done = completedStepCount(line);
                const viewOnly = isViewOnly(line, canGenerate);
                /**
                 * What is stopping this container *now*, not everything it will
                 * eventually need.
                 *
                 * It asked for the union of both gates and asked it at step 0,
                 * so every freshly generated container in the batch wore a "Not
                 * ready" badge — they are generated without a container number
                 * by design, because the driver is being sent to collect a box
                 * nobody has seen. A warning that is true of every row is not a
                 * warning, it is a column. The gate the driver is actually held
                 * to leaves it on the handful that are genuinely stuck.
                 */
                const missing = blockingRequirements(line, done);
                const driver = named(line.driverId as any);
                const plate =
                  line.vehicleId && typeof line.vehicleId === "object"
                    ? vehicleLabel(line.vehicleId)
                    : "";
                // All four read off the container, never off the order it
                // belongs to. A box is created carrying the order's defaults
                // and may be sent to another terminal, loaded elsewhere, or
                // given its own cut-off afterwards — and this table is where
                // the one that diverged has to be visible.
                const route = lineRouteStops(activeStopsOf(line));
                const loadingSite = line.loadingSite?.companyName?.trim();
                const cutOff = cutOffOf(line);
                const truckDriver = [plate, driver].filter(Boolean).join(" · ");

                return (
                  <tr
                    key={line._id}
                    className="border-b border-[rgba(11,15,14,0.05)] last:border-0"
                  >
                    <td className={`${tdFixed} font-medium`}>
                      <button
                        type="button"
                        onClick={() => openLine(line)}
                        title={t(
                          viewOnly
                            ? "linesBlock.viewLine"
                            : "linesBlock.editLine",
                        )}
                        className="rounded text-left underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-[#C97A3C]"
                      >
                        {line.lineNumber}
                      </button>
                      {/* A driver is waiting on an answer about this box.
                          Beside the number rather than in a column of its own:
                          ten columns already compete for this table's width,
                          and what a dispatcher scanning the batch needs is
                          which row to open — not how many messages are on it.
                          The count is on the container already, so the badge
                          costs no query. */}
                      {(line.messaging?.unreadForOffice ?? 0) > 0 && (
                        <span
                          title={line.messaging?.lastMessagePreview || undefined}
                          className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-[#C0392B] px-1.5 py-px align-middle text-[10px] font-semibold text-white"
                        >
                          <MessageCircle className="h-2.5 w-2.5" />
                          {(line.messaging?.unreadForOffice ?? 0) > 9
                            ? "9+"
                            : line.messaging?.unreadForOffice}
                        </span>
                      )}
                    </td>
                    <td className={tdFixed}>{line.containerNumber || EMPTY}</td>
                    <td className={tdFixed}>{line.containerType || EMPTY}</td>
                    {/* Not `tdClip`: the route is the one column here whose
                        value is a list, and a clipped list is a list that has
                        to be opened to be trusted. It wraps within its column
                        and the row grows by a line — see the widths above. */}
                    <td className={td}>
                      <RouteChain stops={route} fallback={<>{EMPTY}</>} />
                    </td>
                    <td className={tdClip} title={loadingSite || undefined}>
                      {loadingSite || EMPTY}
                    </td>
                    <td className={tdFixed}>{cutOff ?? EMPTY}</td>
                    <td className={tdClip} title={truckDriver || undefined}>
                      {truckDriver ? (
                        // min-w-0 so the name ends in an ellipsis rather than
                        // being cut mid-glyph: a flex child will not shrink
                        // below its content without it, and the cell's own
                        // overflow would do the clipping instead.
                        <div className="flex min-w-0 items-center gap-1.5">
                          <Truck className="h-3.5 w-3.5 shrink-0 text-[#8A9590]" />
                          <span className="truncate">{truckDriver}</span>
                        </div>
                      ) : (
                        <span className="text-[#8A9590]">
                          {t("linesBlock.unassigned")}
                        </span>
                      )}
                    </td>
                    {/* The office end of the container, beside the road end.
                        Pressable even on a completed or cancelled container,
                        unlike every other cell here: those are the ones still
                        being invoiced and still needing an owner — see
                        `setLineHandler`, which refuses no status. */}
                    <td className={tdFixed}>
                      <HandlerAssign
                        handler={line.handlerId ?? null}
                        size="sm"
                        label={t("handler.forContainer", {
                          container: line.lineNumber,
                        })}
                        onAssign={async (handlerId) => {
                          const res = await containerOrderAPI.setLineHandler(
                            orderId,
                            line._id,
                            handlerId,
                          );
                          // Under a scope the row may no longer belong here at
                          // all — only the server can say, so re-read.
                          load(1);
                          return res;
                        }}
                        onError={(message) => toast.error(message)}
                      />
                    </td>
                    <td className={tdFixed}>
                      {typeof line.vgmKg === "number"
                        ? `${line.vgmKg} kg`
                        : EMPTY}
                    </td>
                    <td className={td}>
                      {done === 0 ? (
                        <span className="text-[#8A9590]">
                          {t("linesBlock.notStarted")}
                        </span>
                      ) : (
                        <span
                          className="block truncate"
                          title={t(`linesBlock.steps.${steps[done - 1].step}`)}
                        >
                          {done}/{steps.length} ·{" "}
                          {t(`linesBlock.steps.${steps[done - 1].step}`)}
                        </span>
                      )}
                      {/* Why this container is sitting there: the gate it is
                        standing at is refusing, and this row is where anyone
                        scanning a batch of thirty would look.

                        Both gates, not just the first. A container whose driver
                        has reported in and cannot leave the stop is stuck in
                        exactly the way this column exists to show, and it was
                        invisible here — the badge only ever asked about
                        containers that had not started.

                        Neither a `created` nor a `cancelled` container is
                        asked. Both are sitting there for a reason that has
                        nothing to do with their paperwork — one has not been
                        sent out, the other was called off.
                        `blockingRequirements` would answer for them regardless,
                        because it reads the step log and both of them are at
                        step 0. */}
                      {line.status !== "created" &&
                        line.status !== "cancelled" &&
                        missing.length > 0 && (
                        <span
                          title={t(
                            done > 0
                              ? "linesBlock.cannotLeaveDetail"
                              : "linesBlock.notReadyDetail",
                            {
                              fields: missing
                                .map((key) => t(`driver.requirements.${key}`))
                                .join(", "),
                            },
                          )}
                          className="mt-1 flex items-center gap-1 text-[11px] text-[#8A5A28]"
                        >
                          <Lock className="h-3 w-3 shrink-0" />
                          {t(done > 0 ? "linesBlock.cannotLeave" : "linesBlock.notReady")}
                        </span>
                      )}
                    </td>
                    {/* Behind one menu, exactly as the order rows above are.
                        Bare icons in the cell said nothing about what they did
                        until hovered, and there was no room to grow a third:
                        the menu gives every action a label, and a reason
                        whenever one is unavailable. */}
                    <td className={`${td} text-right`}>
                      <ActionMenu
                        ariaLabel={t("linesBlock.columns.actions")}
                        buttonClassName="inline-flex items-center justify-center rounded-md border border-[rgba(11,15,14,0.15)] bg-white p-1.5 text-secondary-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                        items={[
                          {
                            // Eye rather than pencil when the container can
                            // only be read: the label has to promise what the
                            // page will actually do, or the click is a small
                            // betrayal.
                            label: t(
                              viewOnly
                                ? "linesBlock.viewLine"
                                : "linesBlock.editLine",
                            ),
                            icon: viewOnly ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <Pencil className="h-4 w-4" />
                            ),
                            onClick: () => openLine(line),
                          },
                          {
                            label: t("lineCmr.download"),
                            icon:
                              downloadingCmr === line._id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              ),
                            onClick: () => handleDownloadCmr(line),
                            disabled: !!downloadingCmr,
                          },
                          {
                            /**
                             * Cancel — the one that closes a container without
                             * erasing it.
                             *
                             * Offered on a container already under way, unlike
                             * Remove: calling off a box a truck is carrying is
                             * an ordinary thing to have to do, and it is exactly
                             * what the per-container cancel exists for. The two
                             * it is not offered on are a finished run, which is
                             * a record, and a cancelled one, which is already
                             * closed — and `Restore` takes that slot instead.
                             */
                            label: t("linesBlock.cancelLine"),
                            icon: <Ban className="h-4 w-4" />,
                            onClick: () => handleCancel(line),
                            hidden:
                              line.status === "cancelled" ||
                              line.status === "completed",
                            divider: true,
                          },
                          {
                            // Only ever one of these two is shown, so the menu
                            // keeps its height and the action does not move
                            // around under the cursor between rows.
                            label: t("linesBlock.restoreLine"),
                            icon: <RotateCcw className="h-4 w-4" />,
                            onClick: () => handleRestore(line),
                            hidden: line.status !== "cancelled",
                            divider: true,
                          },
                          {
                            // Removal is only offered while nothing has
                            // happened — the server refuses it once a container
                            // is under way or once anything on it has been
                            // billed, and offering an action that will be
                            // refused is worse than not offering it.
                            label: t("linesBlock.removeLine"),
                            icon: <Trash2 className="h-4 w-4" />,
                            onClick: () => handleRemove(line),
                            // Either of the two statuses that mean no wheel has
                            // turned. A container nobody has planned is the
                            // clearest case of all for removal — it exists on
                            // paper and nowhere else.
                            //
                            // The billing rule is not mirrored here and is
                            // deliberately left to the server: it needs the
                            // invoice documents to answer, the panel holds only
                            // the containers, and a client-side guess that said
                            // "removable" over a billed container would be worse
                            // than the 409 that follows.
                            hidden: !UNSTARTED_LINE_STATUSES.includes(line.status),
                            variant: "danger",
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > lines.length && (
        <div className="flex items-center justify-between text-[13px] text-[#8A9590]">
          <span>
            {t("linesBlock.showingCount", { shown: lines.length, total })}
          </span>
          <button
            type="button"
            // The next page appends to what is on screen. It used to replace
            // it: pressing "load more" swapped the first 25 rows for the next
            // 25 while still reporting 25 of 60.
            onClick={() => load(loadedPages + 1)}
            disabled={linesLoading}
            className="rounded-lg border border-gray-300 px-3 py-1.5 hover:bg-[#F5F4F0] disabled:opacity-50"
          >
            {t("linesBlock.loadMore")}
          </button>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
